import { IconKey, IconTrash } from "@tabler/icons-react";
import { useId, useState } from "react";

import {
  errorMessage,
  useAccounts,
  useConsoleKeys,
  useCreateConsoleKey,
  useRevokeConsoleKey,
} from "../../api/queries";
import type { ConsoleConfig, CreatedConsoleKey } from "../../api/types";
import { ALL_ACCOUNTS_SCOPE } from "../../api/types";
import { useStepUp } from "../../auth/useStepUp";
import { formatDateTime, joinWords, pluralize } from "../../lib/format";
import { CopyBlock } from "../ui/CopyBlock";
import { Field } from "../ui/Field";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../ui/StateBlock";

/** The secret, shown once, with the sentence that says it will not come back. */
function NewKey({ minted }: { minted: CreatedConsoleKey }): JSX.Element {
  return (
    <div className="secret-block">
      <p className="secret-block__title">
        Your key — this is the only time it can be shown
      </p>
      <CopyBlock value={minted.key} label="Copy the key" />
      <p className="form__note">
        The console keeps a scrambled copy and the first eight characters, and
        cannot produce this again. Put it in your workspace as{" "}
        <span className="mono">DK_KEY</span>. It reaches{" "}
        {minted.all_accounts
          ? "every client account, including ones created after today"
          : joinWords(minted.accounts)}{" "}
        — and it can read and ask for files, and record a result. It cannot add
        a file, change what a client allows, or erase anything: those are done
        here, in a browser, by a person.
      </p>
    </div>
  );
}

interface ConsoleKeySectionProps {
  config: ConsoleConfig | undefined;
}

/**
 * The keys that can reach a client's files from outside this browser.
 *
 * A key is minted here, shown once, and scoped to named client accounts. A
 * scope is blast radius, not access control: anybody approved reads the whole
 * catalogue in a browser, and the scope exists so that a key found in a git
 * history opens as little as possible.
 *
 * Args:
 *     config: The published configuration, for how many keys may live at once.
 *
 * Returns:
 *     The keys section.
 */
export function ConsoleKeySection({
  config,
}: ConsoleKeySectionProps): JSX.Element {
  const nameId = useId();
  const expiryId = useId();
  const keys = useConsoleKeys();
  const accounts = useAccounts();
  const create = useCreateConsoleKey();
  const revoke = useRevokeConsoleKey();
  const stepUp = useStepUp();

  const [name, setName] = useState("");
  const [everyAccount, setEveryAccount] = useState(false);
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresDays, setExpiresDays] = useState<string>("");
  const [minted, setMinted] = useState<CreatedConsoleKey | null>(null);

  const ready =
    name.trim().length > 0 && (everyAccount || scopes.length > 0);

  const submit = (): void => {
    const payload = {
      name: name.trim(),
      accounts: everyAccount ? [ALL_ACCOUNTS_SCOPE] : scopes,
      expires_days: expiresDays === "" ? null : Number(expiresDays),
    };
    create.mutate(payload, {
      onSuccess: (created) => {
        setMinted(created);
        setName("");
        setScopes([]);
        setEveryAccount(false);
      },
      onError: (error) => {
        stepUp.capture(error, () => submit());
      },
    });
  };

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Your keys</h2>
        {config ? (
          <span className="section__meta">
            {pluralize(config.max_console_keys, "key")} at a time
          </span>
        ) : null}
      </div>

      <p className="section__lead">
        A key is what a workspace carries. It can list the catalogue, ask for a
        file with a purpose, and record a result — and nothing else. Every use
        is recorded against you.
      </p>

      {keys.isPending ? (
        <LoadingBlock label="Reading your keys…" />
      ) : keys.isError ? (
        <ErrorBlock error={keys.error} onRetry={() => void keys.refetch()} />
      ) : keys.data.items.length === 0 ? (
        <EmptyBlock label="You have no keys right now." />
      ) : (
        <ul className="console-keys">
          {keys.data.items.map((item) => (
            <li className="console-keys__row" key={item.id}>
              <span className="console-keys__identity">
                <span className="console-keys__name">
                  <IconKey size={13} aria-hidden="true" /> {item.name}
                </span>
                <span className="console-keys__meta mono">
                  dk_{item.prefix}…
                </span>
                <span className="console-keys__meta">
                  {item.all_accounts
                    ? "every client account, including future ones"
                    : joinWords(item.accounts)}
                </span>
                <span className="console-keys__meta">
                  Made {formatDateTime(item.created_at)} ·{" "}
                  {item.last_used_at
                    ? `last used ${formatDateTime(item.last_used_at)}, to the minute`
                    : "never used"}
                  {item.expires_at
                    ? ` · stops ${formatDateTime(item.expires_at)}`
                    : ""}
                </span>
              </span>
              <button
                type="button"
                className="btn btn--xs btn--danger"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(item.id)}
              >
                <IconTrash size={12} aria-hidden="true" />
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}

      {minted ? <NewKey minted={minted} /> : null}

      <form
        className="form console-key-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field
          label="What is it for?"
          htmlFor={nameId}
          hint="A label you will recognise in six months. Two keys may share one; the first eight characters tell them apart."
        >
          <input
            id={nameId}
            className="input"
            value={name}
            maxLength={64}
            onChange={(event) => setName(event.target.value)}
            placeholder="workspace laptop"
          />
        </Field>

        <fieldset className="form__block">
          <legend className="form__legend">Which clients may it reach?</legend>
          <p className="form__note">
            This is how much a key opens if it ends up somewhere it should not
            be. It is not about what you may see — in this browser you read the
            whole catalogue either way.
          </p>

          {accounts.isPending ? (
            <LoadingBlock label="Reading the client list…" />
          ) : accounts.isError ? (
            <ErrorBlock
              error={accounts.error}
              onRetry={() => void accounts.refetch()}
            />
          ) : (
            <div className="scope-choices">
              {accounts.data.items.map((account) => (
                <label
                  className="check"
                  key={account.id}
                  data-disabled={everyAccount}
                >
                  <input
                    type="checkbox"
                    disabled={everyAccount}
                    checked={scopes.includes(account.slug)}
                    onChange={(event) =>
                      setScopes((current) =>
                        event.target.checked
                          ? [...current, account.slug]
                          : current.filter((slug) => slug !== account.slug),
                      )
                    }
                  />
                  <span>
                    {account.name}{" "}
                    <span className="mono console-keys__meta">
                      {account.slug}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}

          <label className="check check--wide">
            <input
              type="checkbox"
              checked={everyAccount}
              onChange={(event) => setEveryAccount(event.target.checked)}
            />
            <span>
              <strong>Every account, including ones created after today.</strong>{" "}
              The widest a key goes: if this one leaks, it opens every client's
              files this console holds, and the ones we take on next month too.
            </span>
          </label>
        </fieldset>

        <Field
          label="Should it stop working on its own?"
          htmlFor={expiryId}
          hint="Revoking it here kills it at its next request, whatever this says."
        >
          <select
            id={expiryId}
            className="select input--narrow"
            value={expiresDays}
            onChange={(event) => setExpiresDays(event.target.value)}
          >
            <option value="">No, until I revoke it</option>
            <option value="30">After 30 days</option>
            <option value="90">After 90 days</option>
          </select>
        </Field>

        {create.isError && !stepUp.dialog ? (
          <p className="form__error" role="alert">
            {errorMessage(create.error)}
          </p>
        ) : null}

        <p className="form__note">
          The console will ask for your code: a key that reaches a client's data
          is one of the four things it confirms.
        </p>

        <button
          type="submit"
          className="btn btn--primary"
          disabled={create.isPending || !ready}
        >
          {create.isPending ? "Making it…" : "Make a key"}
        </button>
      </form>

      {stepUp.dialog}
    </section>
  );
}
